/**
 * Microsoft Entra ID Authentication Configuration
 * 
 * ALL ENVIRONMENTS: Microsoft Entra ID SSO (including localhost)
 * 
 * Security-hardened: No local authentication bypass
 */

// Always use Microsoft Entra ID authentication for all environments
export const AUTH_MODE = 'entra-id';

console.log(`🔐 Auth Mode: ${AUTH_MODE} (Microsoft Entra ID enforced for all environments)`);

// Sanitize environment variables to avoid whitespace/punctuation issues from key vault / portal values
const sanitizeEnv = (value, fallback) => {
  if (!value || typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const tenantId = sanitizeEnv(process.env.REACT_APP_AZURE_TENANT_ID, '53a5d716-7fec-428f-97f0-51db8b7a9877');
const clientId = sanitizeEnv(process.env.REACT_APP_AZURE_CLIENT_ID, 'ffcc4939-1a64-4f07-8ce7-dfa3b03fd77c');

const isNativePlatform = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true;
const nativePlatform = typeof window !== 'undefined' && window.Capacitor?.getPlatform?.();

const redirectUri =
  isNativePlatform && nativePlatform === 'ios'
    ? 'msauth.com.pyebarkerfs.tagquest://auth'
    : isNativePlatform && nativePlatform === 'android'
      ? 'https://localhost'
      : window.location.origin;

console.log('🧭 Using Entra configuration:', {
  tenantId,
  clientId,
  redirectUri,
  platform: isNativePlatform ? nativePlatform : 'web',
});

export const msalConfig = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    navigateToLoginRequestUrl: !isNativePlatform,
  },
  cache: {
    cacheLocation: isNativePlatform ? 'localStorage' : 'sessionStorage',
    storeAuthStateInCookie: false,
  }
};

export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
  redirectUri,
  onRedirectNavigate: isNativePlatform ? (url) => {
    console.log('📱 iOS: Captured MSAL auth URL for native auth session');
    window.__msalPendingAuthUrl = url;
    return false;
  } : undefined,
};

// App Role Name for Admin Access
// This is defined in the Azure AD Enterprise Application manifest
// Users/groups are assigned this role in Azure AD, and it appears in the ID token
export const ADMIN_ROLE_NAME = sanitizeEnv(process.env.REACT_APP_ADMIN_ROLE, 'Admin');

// Admin Group Object ID (REQUIRED)
// This is the "PBFS Tag Reader App Admins" security group in Entra ID
// Set REACT_APP_ADMIN_GROUP_ID in environment variables
const ADMIN_GROUP_ID = sanitizeEnv(process.env.REACT_APP_ADMIN_GROUP_ID, '');
if (!ADMIN_GROUP_ID) {
  throw new Error("REACT_APP_ADMIN_GROUP_ID is not set (admin group required for all environments)");
}
export { ADMIN_GROUP_ID };

// Local authentication removed - all environments require Microsoft Entra ID

/**
 * Check if user has admin role from ID token claims
 * 
 * App roles are included in the ID token automatically - no Graph API call needed!
 * This is more secure and performant than group-based auth.
 * 
 * The "roles" claim contains an array of role names assigned to the user.
 */
export const isAdminFromClaims = (idTokenClaims) => {
  if (!idTokenClaims) return false;
  
  // Check roles claim (can contain role name OR group IDs when "Emit groups as role claims" is enabled)
  const roles = idTokenClaims.roles || [];
  const hasAdminRole = roles.includes(ADMIN_ROLE_NAME) || roles.includes(ADMIN_GROUP_ID);
  
  // Also check groups claim (fallback if groups not emitted as roles)
  const groups = idTokenClaims.groups || [];
  const isInAdminGroup = groups.includes(ADMIN_GROUP_ID);
  
  const isAdmin = hasAdminRole || isInAdminGroup;
  
  if (isAdmin) {
    console.log(`✅ Admin verified from ID token (group ID: ${ADMIN_GROUP_ID.substring(0, 8)}...)`);
  } else {
    console.log(`❌ User does not have admin role. Roles: ${roles.slice(0, 3).join(', ')}${roles.length > 3 ? '...' : ''}`);
    console.log(`   Looking for group ID: ${ADMIN_GROUP_ID}`);
  }
  
  return isAdmin;
};

/**
 * DEPRECATED: Check if user has overage (no longer needed with app roles)
 * 
 * App roles don't have the "overage" problem that groups do.
 * If a user is in 200+ groups, Azure AD can't fit them all in the token.
 * But app roles are always included in the token, so we don't need Graph API.
 */
export const hasOverage = (idTokenClaims) => {
  // Always return false - we don't use groups anymore
  return false;
};

/**
 * DEPRECATED: Check admin status via Microsoft Graph API
 * 
 * No longer needed! App roles are included directly in the ID token.
 * This eliminates the need for:
 * - Graph API permissions (GroupMember.Read.All)
 * - Additional network calls
 * - Handling overage scenarios
 * 
 * Kept for backwards compatibility but always returns false.
 */
export const isAdminViaGraph = async (accessToken) => {
  console.warn('⚠️ isAdminViaGraph is deprecated - using app roles instead');
  return false;
};


